import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowUpRight, Users, BookOpen, Sparkles } from "lucide-react";

export default function JoinByCode() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    api.get(`/join/${code}`)
      .then(r => setPreview(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Invalid link"));
  }, [code]);

  const redeem = async () => {
    if (!user) {
      nav(`/signup?join=${code}`);
      return;
    }
    setRedeeming(true);
    try {
      const r = await api.post(`/join/${code}/redeem`);
      if (r.data.status === "already_enrolled") toast.info("You&apos;re already enrolled in this course");
      else toast.success("You&apos;re in! Enjoy the course.");
      nav(`/learn/${r.data.course_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not redeem");
    } finally {
      setRedeeming(false);
    }
  };

  // Auto-redeem after a fresh signup with ?join=<code>
  useEffect(() => {
    if (loading || !user || !preview) return;
    const flag = sessionStorage.getItem("forge_auto_redeem");
    if (flag === code) {
      sessionStorage.removeItem("forge_auto_redeem");
      // Defer so setState during render isn't triggered
      const t = setTimeout(() => { redeem(); }, 0);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, preview]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="font-serif text-5xl mb-4">Forge<span className="text-[#E63946]">.</span></div>
          <div className="editorial-card-static p-10">
            <div className="font-serif text-3xl mb-3">Link not found.</div>
            <div className="text-[#52525B] mb-6">{error}. The link may have been disabled by your company admin.</div>
            <Link to="/" className="btn-primary">Back home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!preview) return <div className="min-h-screen flex items-center justify-center"><div className="overline">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-[#F9F9F8] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={preview.course?.thumbnail || "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0A]/40" />
        <Link to="/" className="absolute top-8 left-8 text-white font-serif text-3xl" data-testid="join-brand">Forge<span className="text-[#E63946]">.</span></Link>
        <div className="absolute bottom-12 left-8 right-8 text-white">
          <div className="overline text-[#E63946] mb-3">Company training</div>
          <p className="font-serif text-3xl leading-tight max-w-md">{`You've been invited to a course by ${preview.org_name}.`}</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="font-serif text-3xl">Forge<span className="text-[#E63946]">.</span></Link>
          </div>
          <div className="overline mb-3">{preview.org_name}</div>
          <h1 className="editorial-h1 text-4xl mb-2">{preview.course?.title}</h1>
          <p className="text-[#52525B] mt-3 mb-6 leading-relaxed">{preview.course?.description}</p>

          <div className="editorial-card-static p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 overline"><Users size={14} strokeWidth={1.5}/> Seats available</div>
              {preview.seats_total >= 999999 ? (
                <div className="font-display font-bold text-[#E63946]">unlimited</div>
              ) : (
                <div className="font-display font-bold">{preview.seats_available}<span className="text-[#52525B]">/{preview.seats_total}</span></div>
              )}
            </div>
            {preview.seats_total < 999999 && (
              <div className="h-1 bg-[#E4E4E7]"><div className="h-1 bg-[#E63946]" style={{ width: `${100 * preview.seats_used / Math.max(preview.seats_total, 1)}%` }} /></div>
            )}
          </div>

          {loading ? (
            <div className="overline">Checking…</div>
          ) : !user ? (
            <>
              <p className="text-sm text-[#52525B] mb-4">{"Create your free Forge learner account to claim a seat. You'll be auto-enrolled in this course."}</p>
              <Link to={`/signup?join=${code}`} data-testid="join-signup" className="btn-primary w-full justify-center">Create account & join <ArrowUpRight size={16} strokeWidth={1.5}/></Link>
              <div className="text-sm text-[#52525B] mt-4 text-center">Already have an account? <Link to={`/login?next=/join/${code}`} className="text-[#0A0A0A] underline">Sign in</Link></div>
            </>
          ) : (
            <button onClick={redeem} disabled={redeeming || preview.seats_available <= 0} data-testid="join-redeem" className="btn-primary w-full justify-center">
              {redeeming ? "Joining…" : preview.seats_available <= 0 ? "No seats remaining" : <>Claim your seat <Sparkles size={14}/></>}
            </button>
          )}

          <div className="mt-10 text-xs text-[#52525B] overline">
            Powered by Forge LMS · Tracked completion · Certificate on finish
          </div>
        </div>
      </div>
    </div>
  );
}
